import { useState, useEffect } from 'react';
import { insertSchemaOrg, getHomePageSchema } from '../../utils/seo';

export default function Home() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  useEffect(() => {
    // Insert Schema.org JSON-LD for SEO
    insertSchemaOrg(getHomePageSchema());
  }, []);

  const toggleFaq = (index: number) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  const faqs = [
    {
      question: 'O aluguel do Munck inclui operador?',
      answer: 'Sim! Todos os nossos serviços incluem operador certificado NR-11, NR-12 e NR-35, com vasta experiência em operações de içamento. A segurança e qualidade do serviço começam com profissionais capacitados.'
    },
    {
      question: 'Como é calculado o valor do orçamento?',
      answer: 'O orçamento considera: tipo de operação, peso e dimensões da carga, distância de deslocamento, tempo estimado de operação, complexidade do acesso ao local. Por isso pedimos essas informações no primeiro contato - para oferecer um preço justo e preciso.'
    },
    {
      question: 'Vocês atendem em finais de semana ou feriados?',
      answer: 'Sim, atendemos conforme disponibilidade de agenda. Para serviços urgentes em finais de semana ou feriados, entre em contato o quanto antes para verificarmos a possibilidade de mobilização.'
    },
    {
      question: 'É necessária uma visita técnica antes do serviço?',
      answer: 'Na maioria dos casos, não. Com as informações corretas (tipo de carga, peso, altura necessária, fotos do local), conseguimos planejar e executar com segurança. Em casos específicos ou operações complexas, podemos recomendar uma visita prévia.'
    },
    {
      question: 'Quais informações devo enviar no WhatsApp para pedir um orçamento?',
      answer: 'Para agilizar, envie: 1) Tipo de carga/serviço (ex: transformador 15 ton, poste 12m) 2) Local da operação (cidade/região) 3) Peso estimado 4) Altura/alcance necessário 5) Data desejada 6) Duração estimada. Se possível, envie fotos do local e da carga.'
    },
    {
      question: 'Quais são as limitações de capacidade e alcance do Munck?',
      answer: 'Nosso Munck IMAP IM 20-9,5/22 possui capacidade máxima de 20 toneladas, alcance vertical de até 11,28 metros e alcance horizontal de até 9,52 metros. A capacidade real varia conforme a distância - quanto mais distante, menor a capacidade de carga (seguindo a tabela do fabricante).'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Navbar */}
      <nav className="fixed top-0 left-0 right-0 z-50 transition-all duration-300 bg-white/95 backdrop-blur-sm shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between">
          <img 
            alt="Satori Energia" 
            className="h-8 sm:h-10 object-contain" 
            src="https://static.readdy.ai/image/402fb013a15abdbe1b740b2d8ca8aa4c/107bdeca5d3137440a917d6bc5b4d98f.png"
          />
          <a 
            href="https://wa.me/5589933009903?text=Ol%C3%A1%2C%20gostaria%20de%20um%20or%C3%A7amento%20para%20aluguel%20de%20caminh%C3%A3o%20Munck."
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-primary-500 hover:bg-primary-600 text-white font-semibold px-4 sm:px-6 py-2 sm:py-3 rounded-lg text-xs sm:text-sm shadow-md hover:shadow-lg transition-all duration-300 whitespace-nowrap cursor-pointer"
          >
            <i className="ri-whatsapp-fill text-lg sm:text-xl w-4 sm:w-5 h-4 sm:h-5 flex items-center justify-center"></i>
            <span className="hidden sm:inline">Solicitar Orçamento</span>
            <span className="sm:hidden">Orçamento</span>
          </a>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 overflow-hidden pt-16 sm:pt-20">
        <div className="absolute inset-0 z-0">
          <img 
            alt="Caminhão Munck em Operação" 
            className="w-full h-full object-cover opacity-50" 
            src="https://static.readdy.ai/image/402fb013a15abdbe1b740b2d8ca8aa4c/009e793935aa26c9866df030f5bf34f5.jpeg"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-900/80 via-slate-900/60 to-slate-900/80"></div>
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 py-12 sm:py-20 w-full">
          <div className="max-w-4xl">
            <div className="flex flex-wrap gap-2 sm:gap-3 mb-6 sm:mb-8">
              <span className="px-3 sm:px-4 py-1.5 sm:py-2 bg-primary-500/20 border border-primary-500/50 rounded-full text-primary-300 text-xs sm:text-sm font-semibold">
                Operadores Certificados
              </span>
              <span className="px-3 sm:px-4 py-1.5 sm:py-2 bg-primary-500/20 border border-primary-500/50 rounded-full text-primary-300 text-xs sm:text-sm font-semibold">
                Atendimento 500km de Picos-PI
              </span>
            </div>
            
            <h1 className="text-3xl sm:text-4xl lg:text-6xl xl:text-7xl font-bold text-white mb-4 sm:mb-6 leading-tight">
              Içamento <span className="text-primary-500">Sem Parar</span> Sua Obra: Munck Profissional em Picos-PI
            </h1>
            
            <p className="text-base sm:text-lg lg:text-xl xl:text-2xl text-gray-300 mb-8 sm:mb-10 max-w-3xl">
              Chega de perder dinheiro com obra parada por equipamento quebrado ou operador despreparado.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 mb-12 sm:mb-16">
              <a 
                href="https://wa.me/5589933009903?text=Ol%C3%A1%2C%20gostaria%20de%20um%20or%C3%A7amento%20para%20aluguel%20de%20caminh%C3%A3o%20Munck."
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 sm:gap-3 bg-primary-500 hover:bg-primary-600 text-white font-bold px-6 sm:px-8 py-4 sm:py-5 rounded-xl text-base sm:text-lg shadow-xl hover:shadow-2xl transition-all duration-300 hover:-translate-y-1 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-whatsapp-fill text-xl sm:text-2xl w-5 sm:w-6 h-5 sm:h-6 flex items-center justify-center"></i>
                <span className="hidden sm:inline">SOLICITAR ORÇAMENTO EM 30 MIN</span>
                <span className="sm:hidden">ORÇAMENTO EM 30 MIN</span>
              </a>
            </div>
            
            <div className="flex flex-wrap gap-4 sm:gap-8 text-sm sm:text-base text-gray-400">
              <div className="flex items-center gap-2">
                <i className="ri-time-line text-primary-500 w-4 sm:w-5 h-4 sm:h-5 flex items-center justify-center"></i>
                <span>Orçamento em 30min</span>
              </div>
              <div className="flex items-center gap-2">
                <i className="ri-shield-check-line text-primary-500 w-4 sm:w-5 h-4 sm:h-5 flex items-center justify-center"></i>
                <span>Garantia de Pontualidade</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 3 Pesadelos Section */}
      <section className="py-12 sm:py-16 lg:py-28 bg-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12 sm:mb-16">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl xl:text-5xl font-bold text-white mb-3 sm:mb-4 px-2">
              Os 3 Pesadelos de Todo Gestor de Obra
            </h2>
            <p className="text-base sm:text-lg lg:text-xl text-gray-400 max-w-2xl mx-auto px-4">
              Se você já passou por alguma dessas situações, sabe o quanto custa caro no final
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-6 sm:gap-8">
            <div className="relative p-6 sm:p-8 rounded-2xl bg-slate-800/50 border border-slate-700 hover:border-primary-500/50 transition-all duration-300">
              <div className="w-12 sm:w-14 h-12 sm:h-14 rounded-xl bg-red-500/20 flex items-center justify-center mb-4 sm:mb-6">
                <i className="ri-tools-line text-red-400 text-2xl sm:text-3xl w-6 sm:w-8 h-6 sm:h-8 flex items-center justify-center"></i>
              </div>
              <h3 className="text-xl sm:text-2xl font-bold text-white mb-3 sm:mb-4">
                O Munck Baratinho Que Quebra No Meio da Obra
              </h3>
              <p className="text-sm sm:text-base text-gray-400 leading-relaxed">
                Atraso de dias, multa contratual, retrabalho e prejuízo financeiro que supera qualquer economia inicial.
              </p>
              <div className="absolute top-4 right-4 text-5xl sm:text-6xl font-bold text-slate-700/30">01</div>
            </div>
            
            <div className="relative p-6 sm:p-8 rounded-2xl bg-slate-800/50 border border-slate-700 hover:border-primary-500/50 transition-all duration-300">
              <div className="w-12 sm:w-14 h-12 sm:h-14 rounded-xl bg-red-500/20 flex items-center justify-center mb-4 sm:mb-6">
                <i className="ri-shield-cross-line text-red-400 text-2xl sm:text-3xl w-6 sm:w-8 h-6 sm:h-8 flex items-center justify-center"></i>
              </div>
              <h3 className="text-xl sm:text-2xl font-bold text-white mb-3 sm:mb-4">
                Operador Sem Certificação = Risco de Acidente
              </h3>
              <p className="text-sm sm:text-base text-gray-400 leading-relaxed">
                Embargo da obra, responsabilidade civil, processos trabalhistas e imagem destruída perante clientes.
              </p>
              <div className="absolute top-4 right-4 text-5xl sm:text-6xl font-bold text-slate-700/30">02</div>
            </div>
            
            <div className="relative p-6 sm:p-8 rounded-2xl bg-slate-800/50 border border-slate-700 hover:border-primary-500/50 transition-all duration-300">
              <div className="w-12 sm:w-14 h-12 sm:h-14 rounded-xl bg-red-500/20 flex items-center justify-center mb-4 sm:mb-6">
                <i className="ri-time-line text-red-400 text-2xl sm:text-3xl w-6 sm:w-8 h-6 sm:h-8 flex items-center justify-center"></i>
              </div>
              <h3 className="text-xl sm:text-2xl font-bold text-white mb-3 sm:mb-4">
                Burocracia de Grandes Locadoras
              </h3>
              <p className="text-sm sm:text-base text-gray-400 leading-relaxed">
                Perda de janela de execução, cliente insatisfeito, cronograma comprometido e prejuízo em cascata.
              </p>
              <div className="absolute top-4 right-4 text-5xl sm:text-6xl font-bold text-slate-700/30">03</div>
            </div>
          </div>
          
          <div className="mt-12 sm:mt-16 text-center px-4">
            <p className="text-lg sm:text-xl lg:text-2xl text-white font-medium max-w-3xl mx-auto">
              Você não precisa escolher entre <span className="text-red-400">preço baixo arriscado</span> e <span className="text-red-400">burocracia cara</span>. <span className="text-primary-500 font-bold">Existe uma terceira opção.</span>
            </p>
          </div>
        </div>
      </section>

      {/* Sistema Içamento Clean Section */}
      <section className="py-12 sm:py-16 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12 sm:mb-16">
            <span className="inline-block px-3 sm:px-4 py-1.5 sm:py-2 rounded-full bg-primary-500/10 text-primary-600 font-semibold text-xs sm:text-sm mb-3 sm:mb-4">
              NOSSA METODOLOGIA EXCLUSIVA
            </span>
            <h2 className="text-2xl sm:text-3xl lg:text-5xl xl:text-6xl font-bold text-slate-900 mb-3 sm:mb-4 px-2">
              Sistema <span className="bg-gradient-to-r from-primary-500 to-amber-500 bg-clip-text text-transparent">Içamento Clean™</span> da Satori
            </h2>
            <p className="text-base sm:text-lg lg:text-xl text-gray-600 max-w-2xl mx-auto px-4">
              Enquanto outras locadoras alugam caminhões, nós garantimos que sua obra não para.
            </p>
          </div>
          
          <div className="grid lg:grid-cols-3 gap-6 sm:gap-8">
            <div className="group p-6 sm:p-8 rounded-2xl bg-white border-2 border-slate-200 hover:border-primary-500 transition-all duration-300 hover:shadow-xl">
              <div className="w-14 sm:w-16 h-14 sm:h-16 rounded-2xl bg-gradient-to-br from-primary-500 to-amber-500 flex items-center justify-center mb-4 sm:mb-6 group-hover:scale-110 transition-transform">
                <i className="ri-truck-line text-white text-2xl sm:text-3xl w-6 sm:w-8 h-6 sm:h-8 flex items-center justify-center"></i>
              </div>
              <h3 className="text-xl sm:text-2xl font-bold text-slate-900 mb-2 sm:mb-3">
                Equipamento de Precisão Industrial
              </h3>
              <p className="text-sm sm:text-base text-gray-600 mb-4 sm:mb-6">
                Munck IMAP IM 20-9,5/22 (20 Ton) com manutenção preventiva rigorosa
              </p>
              <ul className="space-y-2 sm:space-y-3">
                <li className="flex items-start gap-2 sm:gap-3">
                  <i className="ri-check-line text-primary-500 text-lg sm:text-xl w-4 sm:w-5 h-4 sm:h-5 flex items-center justify-center flex-shrink-0 mt-0.5"></i>
                  <span className="text-sm sm:text-base text-gray-700">Capacidade vertical: 11,28m</span>
                </li>
                <li className="flex items-start gap-2 sm:gap-3">
                  <i className="ri-check-line text-primary-500 text-lg sm:text-xl w-4 sm:w-5 h-4 sm:h-5 flex items-center justify-center flex-shrink-0 mt-0.5"></i>
                  <span className="text-sm sm:text-base text-gray-700">Alcance horizontal: 9,52m</span>
                </li>
                <li className="flex items-start gap-2 sm:gap-3">
                  <i className="ri-check-line text-primary-500 text-lg sm:text-xl w-4 sm:w-5 h-4 sm:h-5 flex items-center justify-center flex-shrink-0 mt-0.5"></i>
                  <span className="text-sm sm:text-base text-gray-700">Checklist de 47 pontos antes de cada operação</span>
                </li>
              </ul>
            </div>
            
            <div className="group p-6 sm:p-8 rounded-2xl bg-white border-2 border-slate-200 hover:border-primary-500 transition-all duration-300 hover:shadow-xl">
              <div className="w-14 sm:w-16 h-14 sm:h-14 rounded-2xl bg-gradient-to-br from-primary-500 to-amber-500 flex items-center justify-center mb-4 sm:mb-6 group-hover:scale-110 transition-transform">
                <i className="ri-user-star-line text-white text-2xl sm:text-3xl w-6 sm:w-8 h-6 sm:h-8 flex items-center justify-center"></i>
              </div>
              <h3 className="text-xl sm:text-2xl font-bold text-slate-900 mb-2 sm:mb-3">
                Operadores Especialistas
              </h3>
              <p className="text-sm sm:text-base text-gray-600 mb-4 sm:mb-6">
                Não são só motoristas. São técnicos certificados em içamento pesado
              </p>
              <ul className="space-y-2 sm:space-y-3">
                <li className="flex items-start gap-2 sm:gap-3">
                  <i className="ri-check-line text-primary-500 text-lg sm:text-xl w-4 sm:w-5 h-4 sm:h-5 flex items-center justify-center flex-shrink-0 mt-0.5"></i>
                  <span className="text-sm sm:text-base text-gray-700">Certificados NR-11, NR-12, NR-35</span>
                </li>
                <li className="flex items-start gap-2 sm:gap-3">
                  <i className="ri-check-line text-primary-500 text-lg sm:text-xl w-4 sm:w-5 h-4 sm:h-5 flex items-center justify-center flex-shrink-0 mt-0.5"></i>
                  <span className="text-sm sm:text-base text-gray-700">Experiência em energia e estruturas metálicas</span>
                </li>
                <li className="flex items-start gap-2 sm:gap-3">
                  <i className="ri-check-line text-primary-500 text-lg sm:text-xl w-4 sm:w-5 h-4 sm:h-5 flex items-center justify-center flex-shrink-0 mt-0.5"></i>
                  <span className="text-sm sm:text-base text-gray-700">Treinados para resolver problemas no canteiro</span>
                </li>
              </ul>
            </div>
            
            <div className="group p-6 sm:p-8 rounded-2xl bg-white border-2 border-slate-200 hover:border-primary-500 transition-all duration-300 hover:shadow-xl">
              <div className="w-14 sm:w-16 h-14 sm:h-14 rounded-2xl bg-gradient-to-br from-primary-500 to-amber-500 flex items-center justify-center mb-4 sm:mb-6 group-hover:scale-110 transition-transform">
                <i className="ri-timer-line text-white text-2xl sm:text-3xl w-6 sm:w-8 h-6 sm:h-8 flex items-center justify-center"></i>
              </div>
              <h3 className="text-xl sm:text-2xl font-bold text-slate-900 mb-2 sm:mb-3">
                Protocolo "Zero Atraso"
              </h3>
              <p className="text-sm sm:text-base text-gray-600 mb-4 sm:mb-6">
                Sistema operacional desenhado para eliminar imprevistos
              </p>
              <ul className="space-y-2 sm:space-y-3">
                <li className="flex items-start gap-2 sm:gap-3">
                  <i className="ri-check-line text-primary-500 text-lg sm:text-xl w-4 sm:w-5 h-4 sm:h-5 flex items-center justify-center flex-shrink-0 mt-0.5"></i>
                  <span className="text-sm sm:text-base text-gray-700">Orçamento comercial em até 30 minutos</span>
                </li>
                <li className="flex items-start gap-2 sm:gap-3">
                  <i className="ri-check-line text-primary-500 text-lg sm:text-xl w-4 sm:w-5 h-4 sm:h-5 flex items-center justify-center flex-shrink-0 mt-0.5"></i>
                  <span className="text-sm sm:text-base text-gray-700">Checklist de segurança gratuito pré-operação</span>
                </li>
                <li className="flex items-start gap-2 sm:gap-3">
                  <i className="ri-check-line text-primary-500 text-lg sm:text-xl w-4 sm:w-5 h-4 sm:h-5 flex items-center justify-center flex-shrink-0 mt-0.5"></i>
                  <span className="text-sm sm:text-base text-gray-700">Chegada 15 minutos antes (ou desconto)</span>
                </li>
              </ul>
            </div>
          </div>
          
          <div className="mt-12 sm:mt-16 p-6 sm:p-8 rounded-2xl bg-slate-900 text-center">
            <p className="text-base sm:text-lg lg:text-xl xl:text-2xl text-white font-medium px-2">
              "Enquanto outras locadoras alugam caminhões, <span className="text-primary-500">nós garantimos que sua obra não para.</span>"
            </p>
          </div>
        </div>
      </section>

      {/* 9 Tipos de Içamento Section */}
      <section className="py-12 sm:py-16 lg:py-28 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12 sm:mb-16">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl xl:text-5xl font-bold text-slate-900 mb-3 sm:mb-4 px-2">
              9 Tipos de Içamento Que Dominamos
            </h2>
            <p className="text-base sm:text-lg lg:text-xl text-gray-600 max-w-2xl mx-auto px-4">
              Raio de atendimento: 500km a partir de Picos-PI
            </p>
          </div>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {[
              'Içamento de cargas em geral',
              'Perfuratriz / Apoio para perfuração',
              'Instalação de postes',
              'Movimentação de transformadores',
              'Montagem de pré-moldados',
              'Montagem de estruturas metálicas',
              'Transporte de máquinas pesadas',
              'Remoção de cargas'
            ].map((item, index) => (
              <div key={index} className="group flex items-center gap-3 sm:gap-4 p-4 sm:p-5 rounded-xl bg-white border border-slate-200 hover:border-primary-500 hover:shadow-lg transition-all duration-300">
                <div className="shrink-0 w-10 sm:w-12 h-10 sm:h-12 rounded-xl bg-primary-500/10 flex items-center justify-center group-hover:bg-primary-500 transition-colors duration-300">
                  <i className="ri-check-line text-primary-500 group-hover:text-white text-xl sm:text-2xl w-5 sm:w-6 h-5 sm:h-6 flex items-center justify-center"></i>
                </div>
                <span className="font-medium text-slate-900 text-sm sm:text-base">{item}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Como Funciona o Orçamento Section */}
      <section className="py-12 sm:py-16 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12 sm:mb-16">
            <span className="inline-block px-3 sm:px-4 py-1.5 sm:py-2 rounded-full bg-primary-500/10 text-primary-600 font-semibold text-xs sm:text-sm mb-3 sm:mb-4">
              💰 100% TRANSPARENTE
            </span>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl xl:text-5xl font-bold text-slate-900 mb-3 sm:mb-4 px-2">
              Como Funciona o Orçamento
            </h2>
            <p className="text-base sm:text-lg lg:text-xl text-gray-600 max-w-2xl mx-auto px-4">
              Nosso orçamento é personalizado porque cada operação tem necessidades diferentes. Para responder em 30 minutos, precisamos de algumas informações:
            </p>
          </div>
          
          <div className="mb-12 sm:mb-16">
            <h3 className="text-xl sm:text-2xl lg:text-3xl font-bold text-slate-900 mb-3 sm:mb-4 text-center px-2">
              📝 Checklist: Tenha Essas Informações
            </h3>
            <p className="text-center text-sm sm:text-base text-gray-600 mb-8 sm:mb-10 max-w-2xl mx-auto px-4">
              Quanto mais detalhes você nos passar, mais preciso e rápido será o orçamento:
            </p>
            
            <div className="grid md:grid-cols-2 gap-4 sm:gap-6 max-w-5xl mx-auto">
              {[
                { title: 'Tipo de carga/serviço:', desc: 'Ex: Transformador 15 ton, Poste 12m, Estrutura metálica...' },
                { title: 'Local da operação:', desc: 'Cidade/região (para calcular deslocamento)' },
                { title: 'Peso estimado da carga:', desc: 'Se não souber o peso exato, sem problema! Nos diga o tipo' },
                { title: 'Altura/alcance necessário:', desc: 'Ex: 8 metros de altura, 6 metros horizontal...' },
                { title: 'Prazo/data desejada:', desc: 'Urgente (mesmo dia) ou planejado (semana específica)?' },
                { title: 'Duração estimada:', desc: 'Meio período (4h), diária (8h), múltiplos dias?' }
              ].map((item, index) => (
                <div key={index} className="p-4 sm:p-6 rounded-xl bg-slate-50 border border-slate-200">
                  <div className="flex items-start gap-3 sm:gap-4">
                    <div className="shrink-0 w-8 sm:w-10 h-8 sm:h-10 rounded-lg bg-primary-500/10 flex items-center justify-center">
                      <i className="ri-checkbox-circle-line text-primary-500 text-lg sm:text-xl w-4 sm:w-5 h-4 sm:h-5 flex items-center justify-center"></i>
                    </div>
                    <div>
                      <h4 className="font-bold text-slate-900 mb-1 sm:mb-2 text-sm sm:text-base">{item.title}</h4>
                      <p className="text-xs sm:text-sm text-gray-600">{item.desc}</p>
                    </div>
                  </div>
                </div>
              ))}
              
              <div className="p-4 sm:p-6 rounded-xl bg-slate-50 border border-slate-200 md:col-span-2">
                <div className="flex items-start gap-3 sm:gap-4">
                  <div className="shrink-0 w-8 sm:w-10 h-8 sm:h-10 rounded-lg bg-primary-500/10 flex items-center justify-center">
                    <i className="ri-checkbox-circle-line text-primary-500 text-lg sm:text-xl w-4 sm:w-5 h-4 sm:h-5 flex items-center justify-center"></i>
                  </div>
                  <div>
                    <h4 className="font-bold text-slate-900 mb-1 sm:mb-2 text-sm sm:text-base">Complexidade do acesso:</h4>
                    <p className="text-xs sm:text-sm text-gray-600">Local aberto ou área confinada? Precisa de manobras especiais?</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <div className="max-w-4xl mx-auto mb-12 sm:mb-16 p-6 sm:p-8 rounded-2xl bg-gradient-to-br from-orange-50 to-amber-50 border border-orange-200">
            <div className="flex items-start gap-3 sm:gap-4">
              <div className="shrink-0 w-10 sm:w-12 h-10 sm:h-12 rounded-xl bg-primary-500 flex items-center justify-center">
                <i className="ri-lightbulb-line text-white text-xl sm:text-2xl w-5 sm:w-6 h-5 sm:h-6 flex items-center justify-center"></i>
              </div>
              <div>
                <h4 className="text-lg sm:text-xl lg:text-2xl font-bold text-slate-900 mb-2 sm:mb-3">
                  💡 Não Sabe Todas As Respostas?
                </h4>
                <p className="text-sm sm:text-base text-gray-700 mb-3 sm:mb-4">
                  Entre em contato mesmo assim. Fazemos uma análise técnica rápida com você.
                </p>
                <p className="text-sm sm:text-base text-gray-700">
                  Nosso time tem experiência em orientar sobre o melhor setup para cada operação.
                </p>
              </div>
            </div>
          </div>
          
          <div className="max-w-4xl mx-auto">
            <div className="p-6 sm:p-8 rounded-2xl bg-slate-900 text-white">
              <div className="text-center mb-6 sm:mb-8">
                <div className="inline-flex items-center gap-2 sm:gap-3 mb-3 sm:mb-4">
                  <i className="ri-time-line text-primary-500 text-3xl sm:text-4xl w-8 sm:w-10 h-8 sm:h-10 flex items-center justify-center"></i>
                  <h3 className="text-xl sm:text-2xl lg:text-3xl font-bold">Resposta em 30 Minutos</h3>
                </div>
              </div>
              
              <div className="space-y-3 sm:space-y-4">
                {[
                  'Você manda mensagem no WhatsApp',
                  'Analisamos tecnicamente em 30 min',
                  'Enviamos orçamento + disponibilidade',
                  'Se aprovar, agendamos e enviamos checklist'
                ].map((step, index) => (
                  <div key={index} className="flex items-start gap-3 sm:gap-4">
                    <div className="shrink-0 w-7 sm:w-8 h-7 sm:h-8 rounded-full bg-primary-500 flex items-center justify-center text-sm font-bold">
                      {index + 1}
                    </div>
                    <p className="text-sm sm:text-base text-gray-300 pt-1">{step}</p>
                  </div>
                ))}
              </div>
              
              <div className="mt-6 sm:mt-8 pt-6 sm:pt-8 border-t border-slate-700 text-center">
                <p className="text-lg sm:text-xl lg:text-2xl font-bold text-primary-500 mb-4 sm:mb-6 px-2">
                  Sem burocracia. Resposta rápida.
                </p>
                <a 
                  href="https://wa.me/5589933009903?text=Ol%C3%A1%2C%20gostaria%20de%20um%20or%C3%A7amento%20para%20aluguel%20de%20caminh%C3%A3o%20Munck."
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 sm:gap-3 bg-primary-500 hover:bg-primary-600 text-white font-bold px-6 sm:px-8 py-3 sm:py-4 rounded-xl text-base sm:text-lg shadow-xl hover:shadow-2xl transition-all duration-300 hover:-translate-y-1 whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-whatsapp-fill text-xl sm:text-2xl w-5 sm:w-6 h-5 sm:h-6 flex items-center justify-center"></i>
                  <span className="hidden sm:inline">PEDIR ORÇAMENTO AGORA</span>
                  <span className="sm:hidden">ORÇAMENTO AGORA</span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Capacidade Técnica Section */}
      <section className="py-12 sm:py-16 lg:py-28 bg-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid lg:grid-cols-2 gap-8 sm:gap-12 items-center">
            <div>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl xl:text-5xl font-bold text-white mb-3 sm:mb-4">
                Capacidade Técnica Comprovada
              </h2>
              <p className="text-base sm:text-lg lg:text-xl text-gray-400 mb-6 sm:mb-8">
                Equipamento homologado para operações industriais de alto risco.
              </p>
              
              <div className="grid sm:grid-cols-2 gap-3 sm:gap-4">
                {[
                  { icon: 'ri-truck-line', label: 'Modelo', value: 'Munck IMAP IM 20-9,5/22' },
                  { icon: 'ri-weight-line', label: 'Capacidade', value: '20 Toneladas' },
                  { icon: 'ri-arrow-up-line', label: 'Alcance vertical', value: '11,28 metros' },
                  { icon: 'ri-arrow-right-line', label: 'Alcance horizontal', value: '9,52 metros' },
                  { icon: 'ri-shield-check-line', label: 'Certificações', value: 'INMETRO, NR-11, NR-12' },
                  { icon: 'ri-star-line', label: 'Diferenciais', value: 'Sinalização + Equipe' }
                ].map((item, index) => (
                  <div key={index} className="flex items-start gap-3 sm:gap-4 p-3 sm:p-4 rounded-xl bg-slate-800/50">
                    <div className="shrink-0 w-8 sm:w-10 h-8 sm:h-10 rounded-lg bg-primary-500/20 flex items-center justify-center">
                      <i className={`${item.icon} text-orange-400 text-lg sm:text-xl w-4 sm:w-5 h-4 sm:h-5 flex items-center justify-center`}></i>
                    </div>
                    <div>
                      <p className="text-xs sm:text-sm text-gray-400">{item.label}</p>
                      <p className="font-semibold text-white text-sm sm:text-base">{item.value}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="relative">
              <div className="p-6 sm:p-8 rounded-3xl bg-gradient-to-br from-slate-800 to-slate-900 border border-slate-700">
                <div className="text-center mb-6 sm:mb-8">
                  <span className="text-5xl sm:text-6xl lg:text-7xl font-bold text-primary-500">20</span>
                  <p className="text-xl sm:text-2xl font-bold text-white mt-2">TONELADAS</p>
                </div>
                
                <div className="grid grid-cols-2 gap-4 sm:gap-6 text-center">
                  <div className="p-4 sm:p-6 rounded-xl bg-slate-800/50">
                    <p className="text-2xl sm:text-3xl font-bold text-primary-500">11,28m</p>
                    <p className="text-xs sm:text-sm text-gray-400">Alcance Vertical</p>
                  </div>
                  <div className="p-4 sm:p-6 rounded-xl bg-slate-800/50">
                    <p className="text-2xl sm:text-3xl font-bold text-primary-500">9,52m</p>
                    <p className="text-xs sm:text-sm text-gray-400">Alcance Horizontal</p>
                  </div>
                </div>
                
                <div className="mt-6 sm:mt-8 pt-4 sm:pt-6 border-t border-slate-700 flex flex-wrap justify-center gap-2 sm:gap-4">
                  <span className="px-3 sm:px-4 py-1.5 sm:py-2 rounded-full bg-green-500/20 text-green-400 text-xs sm:text-sm font-medium">INMETRO</span>
                  <span className="px-3 sm:px-4 py-1.5 sm:py-2 rounded-full bg-green-500/20 text-green-400 text-xs sm:text-sm font-medium">NR-11</span>
                  <span className="px-3 sm:px-4 py-1.5 sm:py-2 rounded-full bg-green-500/20 text-green-400 text-xs sm:text-sm font-medium">NR-12</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Por Que Contratar Section */}
      <section className="py-12 sm:py-16 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12 sm:mb-16">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl xl:text-5xl font-bold text-slate-900 mb-3 sm:mb-4 px-2">
              Por Que Contratar a Satori Energia?
            </h2>
            <p className="text-base sm:text-lg lg:text-xl text-gray-600 max-w-2xl mx-auto px-4">
              Escolha a Satori para garantir excelência e segurança
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
            {[
              { icon: 'ri-shield-check-line', title: 'Segurança em Primeiro Lugar', desc: 'Operadores experientes e equipamentos revisados.' },
              { icon: 'ri-flashlight-line', title: 'Agilidade e Eficiência', desc: 'Mobilização rápida para cumprir seus prazos.' },
              { icon: 'ri-team-line', title: 'Equipe Especializada', desc: 'Profissionais treinados para todas as operações.' },
              { icon: 'ri-checkbox-circle-line', title: 'Serviço Completo', desc: 'Inclui operador, sinalização e apoio logístico.' },
              { icon: 'ri-map-pin-line', title: 'Atendimento Regional', desc: 'Conhecimento da região de Picos-PI.' },
              { icon: 'ri-file-list-line', title: 'Orçamento Personalizado', desc: 'Soluções sob medida para sua necessidade.' }
            ].map((item, index) => (
              <div key={index} className="p-5 sm:p-6 rounded-xl bg-slate-50 hover:bg-slate-100 transition-colors">
                <div className="w-12 sm:w-14 h-12 sm:h-14 rounded-xl bg-primary-500/10 flex items-center justify-center mb-3 sm:mb-4">
                  <i className={`${item.icon} text-primary-500 text-xl sm:text-2xl w-5 sm:w-6 h-5 sm:h-6 flex items-center justify-center`}></i>
                </div>
                <h3 className="text-lg sm:text-xl font-bold text-slate-900 mb-2">{item.title}</h3>
                <p className="text-sm sm:text-base text-gray-600">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 sm:py-16 lg:py-28 bg-primary-500">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center">
            <div className="inline-flex items-center gap-2 px-3 sm:px-4 py-1.5 sm:py-2 rounded-full bg-white/20 text-white text-xs sm:text-sm font-medium mb-4 sm:mb-6">
              <i className="ri-time-line w-4 h-4 flex items-center justify-center"></i>
              Agenda Limitada
            </div>
            <h2 className="text-2xl sm:text-3xl lg:text-5xl xl:text-6xl font-bold text-white mb-4 sm:mb-6 px-2">
              Solicite Seu Orçamento Agora
            </h2>
            <p className="text-base sm:text-lg lg:text-xl text-white/90 mb-8 sm:mb-10 max-w-2xl mx-auto px-4">
              Atendimento rápido pelo WhatsApp. Informe local, tipo de carga, peso e altura necessária.
            </p>
            <a 
              href="https://wa.me/5589933009903?text=Ol%C3%A1%2C%20gostaria%20de%20um%20or%C3%A7amento%20para%20aluguel%20de%20caminh%C3%A3o%20Munck."
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 sm:gap-3 bg-white hover:bg-gray-100 text-primary-600 font-bold px-6 sm:px-8 py-4 sm:py-5 rounded-xl text-base sm:text-lg shadow-xl hover:shadow-2xl transition-all duration-300 hover:-translate-y-1 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-whatsapp-fill text-xl sm:text-2xl w-5 sm:w-6 h-5 sm:h-6 flex items-center justify-center"></i>
              <span className="hidden sm:inline">FALAR NO WHATSAPP AGORA</span>
              <span className="sm:hidden">WHATSAPP</span>
            </a>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-12 sm:py-16 lg:py-28 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12 sm:mb-16">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl xl:text-5xl font-bold text-slate-900 mb-3 sm:mb-4 px-2">
              Perguntas Frequentes
            </h2>
            <p className="text-base sm:text-lg lg:text-xl text-gray-600 px-4">
              Tudo que você precisa saber
            </p>
          </div>
          
          <div className="space-y-3 sm:space-y-4">
            {faqs.map((faq, index) => (
              <div key={index} className="bg-white border border-slate-200 rounded-xl overflow-hidden hover:border-primary-500 transition-colors">
                <button 
                  onClick={() => toggleFaq(index)}
                  className="w-full flex justify-between items-center gap-3 sm:gap-4 p-4 sm:p-6 text-left cursor-pointer"
                >
                  <h3 className="text-base sm:text-lg font-bold text-slate-900">{faq.question}</h3>
                  <div className="flex-shrink-0 w-7 sm:w-8 h-7 sm:h-8 rounded-full bg-primary-500/10 flex items-center justify-center">
                    <i className={`${openFaq === index ? 'ri-subtract-line' : 'ri-add-line'} text-primary-500 text-lg sm:text-xl w-4 sm:w-5 h-4 sm:h-5 flex items-center justify-center transition-transform`}></i>
                  </div>
                </button>
                {openFaq === index && (
                  <div className="px-4 sm:px-6 pb-4 sm:pb-6">
                    <p className="text-sm sm:text-base text-gray-600 leading-relaxed">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 sm:py-16 bg-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 sm:gap-12 mb-8 sm:mb-12">
            <div>
              <img 
                alt="Satori Energia Logo" 
                className="h-10 sm:h-12 mb-3 sm:mb-4 object-contain" 
                src="https://static.readdy.ai/image/402fb013a15abdbe1b740b2d8ca8aa4c/107bdeca5d3137440a917d6bc5b4d98f.png"
              />
              <p className="text-sm text-gray-400 leading-relaxed">
                Aluguel de Munck profissional com operador especialista.
              </p>
            </div>
            
            <div>
              <h4 className="text-xs sm:text-sm font-bold uppercase tracking-wider text-gray-500 mb-3 sm:mb-4">Contato</h4>
              <ul className="space-y-2 sm:space-y-3 text-gray-300 text-sm">
                <li className="flex items-center gap-2 sm:gap-3">
                  <i className="ri-map-pin-line w-4 h-4 flex items-center justify-center"></i>
                  Picos - Piauí
                </li>
                <li className="flex items-center gap-2 sm:gap-3">
                  <i className="ri-phone-line w-4 h-4 flex items-center justify-center"></i>
                  (89) 93300-9903
                </li>
                <li className="flex items-center gap-2 sm:gap-3">
                  <i className="ri-instagram-line w-4 h-4 flex items-center justify-center"></i>
                  @satorienergia
                </li>
              </ul>
            </div>
            
            <div>
              <h4 className="text-xs sm:text-sm font-bold uppercase tracking-wider text-gray-500 mb-3 sm:mb-4">Serviços</h4>
              <ul className="space-y-1.5 sm:space-y-2 text-gray-300 text-sm">
                <li>Içamento de Cargas</li>
                <li>Instalação de Postes</li>
                <li>Transporte de Equipamentos</li>
                <li>Montagem de Estruturas</li>
              </ul>
            </div>
            
            <div>
              <h4 className="text-xs sm:text-sm font-bold uppercase tracking-wider text-gray-500 mb-3 sm:mb-4">Área de Atendimento</h4>
              <p className="text-gray-300 text-sm">
                Teresina, Floriano, Paulistana, São Raimundo Nonato e toda região.
              </p>
            </div>
          </div>
          
          <div className="pt-6 sm:pt-8 border-t border-slate-800 flex flex-col sm:flex-row justify-between items-center gap-3 sm:gap-4">
            <p className="text-xs sm:text-sm text-gray-500">
              © 2025 Satori Energia. Todos os direitos reservados.
            </p>
            <a 
              href="https://readdy.ai/?ref=logo" 
              target="_blank" 
              rel="noopener noreferrer" 
              className="text-xs sm:text-sm text-gray-500 hover:text-white transition-colors cursor-pointer"
            >
              Powered by Readdy
            </a>
          </div>
        </div>
      </footer>

      {/* WhatsApp Floating Button */}
      <a 
        href="https://wa.me/5589933009903?text=Ol%C3%A1%2C%20gostaria%20de%20um%20or%C3%A7amento%20para%20aluguel%20de%20caminh%C3%A3o%20Munck."
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-4 sm:bottom-6 right-4 sm:right-6 w-14 sm:w-16 h-14 sm:h-16 bg-green-500 hover:bg-green-600 rounded-full flex items-center justify-center shadow-2xl hover:scale-110 transition-all duration-300 z-50 cursor-pointer"
        title="Fale Conosco"
      >
        <i className="ri-whatsapp-fill text-white text-2xl sm:text-3xl w-6 sm:w-8 h-6 sm:h-8 flex items-center justify-center"></i>
      </a>
    </div>
  );
}
